import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface UserProfile {
  id?: number;
  access_token?: string;
  refresh_token?: string;
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  name?: string;
  profile_picture?: string;
  is_active?: boolean;
  email_verified?: boolean;
  referral_code?: string;
  gender?: string | null;
  marital_status?: boolean | null;
  birthday?: string;
  city?: string | null;
  city_id?: number;
  provider?: string;
  provider_id?: string;
  created_at?: string;
  updated_at?: string;
  last_login?: string;
  [key: string]: any;
  state:string;
  country:string
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_ID_KEY = 'user_id';
  private readonly USER_PROFILE_KEY = 'user_profile';
  private _userProfile: UserProfile | null = null;

  /** Emits when auth state changes (login/logout) so header and other components can react */
  readonly authState$ = new BehaviorSubject<UserProfile | null>(null);

  constructor() {
    // Load token and user profile from localStorage on service initialization
    this.getToken();
    this.loadUserProfile();
  }

  /**
   * Store authentication token.
   * When the token changes (another login / account switch), drop cached profile so UI cannot show the previous user.
   */
  setToken(token: string): void {
    if (!token) return;
    const prev = localStorage.getItem(this.TOKEN_KEY);
    if (prev && prev !== token) {
      localStorage.removeItem(this.USER_PROFILE_KEY);
      this._userProfile = null;
      this.authState$.next(null);
    }
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Get stored authentication token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  setRefreshToken(token: string | null | undefined): void {
    if (!token) return;
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  /**
   * VideoStech / SMS OTP verify shape:
   * `{ code: 1, result: { access_token: { token, expiry }, refresh_token: { token, expiry } } }`
   * Also accepts flat `token` / `access_token` for older APIs.
   */
  applyAuthApiResult(response: unknown): boolean {
    const r = response as Record<string, unknown> | null;
    if (!r || r['code'] !== 1) {
      return false;
    }

    const result = (r['result'] ?? r['data']) as Record<string, unknown> | undefined;
    if (result && typeof result === 'object') {
      const accessWrap = result['access_token'] as Record<string, unknown> | string | undefined;
      const accessToken =
        typeof accessWrap === 'string'
          ? accessWrap
          : accessWrap && typeof accessWrap === 'object'
            ? (accessWrap['token'] as string | undefined)
            : undefined;

      if (accessToken && typeof accessToken === 'string') {
        this.setToken(accessToken);
        const refreshWrap = result['refresh_token'] as Record<string, unknown> | string | undefined;
        const refreshToken =
          typeof refreshWrap === 'string'
            ? refreshWrap
            : refreshWrap && typeof refreshWrap === 'object'
              ? (refreshWrap['token'] as string | undefined)
              : undefined;
        if (refreshToken) {
          this.setRefreshToken(refreshToken);
        }
        return true;
      }
    }

    const flat =
      (r['token'] as string | undefined) ??
      (r['access_token'] as string | undefined) ??
      (typeof r['access_token'] === 'object' && r['access_token']
        ? ((r['access_token'] as Record<string, unknown>)['token'] as string | undefined)
        : undefined);

    if (flat && typeof flat === 'string') {
      this.setToken(flat);
      return true;
    }

    return false;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * For JWT-shaped tokens: true when `exp` is in the past (with optional leeway in seconds).
   * For non-JWT tokens, returns false so expiry relies on API 401 handling.
   */
  isAccessTokenExpired(leewaySeconds: number = 60): boolean {
    const token = this.getToken();
    if (!token) {
      return true;
    }
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }
    try {
      let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const pad = base64.length % 4;
      if (pad) {
        base64 += '='.repeat(4 - pad);
      }
      const payload = JSON.parse(atob(base64)) as { exp?: number };
      if (typeof payload.exp !== 'number') {
        return false;
      }
      const nowSec = Math.floor(Date.now() / 1000);
      return nowSec >= payload.exp - leewaySeconds;
    } catch {
      return false;
    }
  }

  /**
   * Store user ID
   */
  setUserId(userId: number): void {
    if (userId) {
      localStorage.setItem(this.USER_ID_KEY, userId.toString());
    }
  }

  /**
   * Get stored user ID
   */
  getUserId(): number | null {
    const userId = localStorage.getItem(this.USER_ID_KEY);
    return userId ? parseInt(userId, 10) : null;
  }

  /**
   * Store user profile data
   */
  setUserProfile(profile: UserProfile): void {
    this._userProfile = profile;
    localStorage.setItem(this.USER_PROFILE_KEY, JSON.stringify(profile));
    this.authState$.next(profile);
  }

  /**
   * Get stored user profile
   */
  getUserProfile(): UserProfile | null {
    if (!this._userProfile) {
      this.loadUserProfile();
    }
    return this._userProfile;
  }

  /**
   * Read cached profile without emitting `authState$`.
   * Useful to avoid feedback loops when a component is already reacting to `authState$`.
   */
  peekUserProfile(): UserProfile | null {
    if (this._userProfile) return this._userProfile;
    const profileStr = localStorage.getItem(this.USER_PROFILE_KEY);
    if (!profileStr) return null;
    try {
      this._userProfile = JSON.parse(profileStr);
      return this._userProfile;
    } catch {
      return null;
    }
  }

  /**
   * Load user profile from localStorage
   */
  private loadUserProfile(): void {
    const profileStr = localStorage.getItem(this.USER_PROFILE_KEY);
    if (profileStr) {
      try {
        this._userProfile = JSON.parse(profileStr);
      } catch (e) {
        console.error('Error parsing user profile:', e);
        this._userProfile = null;
      }
    }
    this.authState$.next(this._userProfile);
  }

  /**
   * Clear authentication data (logout)
   */
  clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_ID_KEY);
    localStorage.removeItem(this.USER_PROFILE_KEY);
    this._userProfile = null;
    this.authState$.next(null);
  }

  /**
   * Store authentication data from OTP verification response
   */
  setAuthData(response: any): void {
    if (response.token) {
      this.setToken(response.token);
    }
    if (response.userId) {
      this.setUserId(response.userId);
    }
  }
}
